import type { Gateway3P } from '../ScopingEditor/useGetState';
import { type Dt3pDeviceStatus, DT3P_STATUS_COLORS } from '../ScopingEditor/dt3pStatus';
import { SVGProps, memo } from 'react';
import { AutoTooltip } from '@/components';
import { ICONS_SPRITE_URL } from '@/components/primitives/icon/Icon';
import { useZoom } from '../ZoomContext';
import { useCanvasColors } from './canvasColors';

export const GATEWAY3P_ICON_SIZE = 1.25;

// Fixed IDs for the single shared set of gateway3p filters.
// All Gateway3pItem instances reference these instead of creating per-instance <defs>.
const GATEWAY3P_FILTER_NORMAL_ID = 'gateway3p-filter-normal';
const GATEWAY3P_FILTER_SELECTED_ID = 'gateway3p-filter-selected';
const GATEWAY3P_FILTER_STATUS_PLACEHOLDER_ID = 'gateway3p-filter-status-placeholder';
const GATEWAY3P_FILTER_STATUS_WARNING_ID = 'gateway3p-filter-status-warning';
const GATEWAY3P_FILTER_STATUS_CONNECTED_ID = 'gateway3p-filter-status-connected';
const GATEWAY3P_FILTER_STATUS_ERROR_ID = 'gateway3p-filter-status-error';

const STATUS_FILTER_ID: Record<Dt3pDeviceStatus, string> = {
  placeholder: GATEWAY3P_FILTER_STATUS_PLACEHOLDER_ID,
  warning: GATEWAY3P_FILTER_STATUS_WARNING_ID,
  connected: GATEWAY3P_FILTER_STATUS_CONNECTED_ID,
  error: GATEWAY3P_FILTER_STATUS_ERROR_ID,
};

/**
 * Renders a single shared <defs> block with the gateway3p color filters.
 * Mount this once inside the parent SVG so all Gateway3pItem instances can
 * reference the filters by ID without duplicating them.
 */
export function Gateway3pFilters() {
  const colors = useCanvasColors();
  return (
    <defs>
      {/* Normal: recolor icon to zone idle gray (fallback, superseded by status filters) */}
      <filter id={GATEWAY3P_FILTER_NORMAL_ID}>
        <feFlood floodColor={colors.roomStrokeIdle} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Selected: recolor icon using theme-aware stroke color */}
      <filter id={GATEWAY3P_FILTER_SELECTED_ID}>
        <feFlood floodColor={colors.roomStroke} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: placeholder — not yet configured */}
      <filter id={GATEWAY3P_FILTER_STATUS_PLACEHOLDER_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.placeholder} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: warning — external_gateway_id set but not yet provisioned */}
      <filter id={GATEWAY3P_FILTER_STATUS_WARNING_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.warning} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: connected — provisioned and sensors heartbeating */}
      <filter id={GATEWAY3P_FILTER_STATUS_CONNECTED_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.connected} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: error — provisioned but no sensor heartbeat within 30 min */}
      <filter id={GATEWAY3P_FILTER_STATUS_ERROR_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.error} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
    </defs>
  );
}

function Gateway3pItem({
  x = 0,
  y = 0,
  name,
  id,
  status = 'placeholder',
  selected,
  selecting = false,
  fade = false,
  locked = false,
  onClick,
  ...rest
}: {
  selected: boolean;
  status?: Dt3pDeviceStatus;
  selecting?: boolean;
  fade?: boolean;
  locked?: boolean;
  id: string;
} & SVGProps<SVGGElement> &
  Pick<Gateway3P, 'name'> & { x?: number; y?: number }) {
  const { adjustedZoom } = useZoom();
  const size = GATEWAY3P_ICON_SIZE / adjustedZoom;
  const showAsSelected = selected || selecting;
  const filterId = showAsSelected ? GATEWAY3P_FILTER_SELECTED_ID : STATUS_FILTER_ID[status];

  return (
    <g
      data-id="gateway3p-item"
      id={id}
      onClick={onClick}
      transform={`translate(${x}, ${y})`}
      style={{ pointerEvents: locked ? 'none' : 'all', opacity: fade ? 0.5 : 1 }}
      data-selected={selected}
      {...rest}
    >
      <AutoTooltip
        label={name}
        placement="top"
        keepOpenOnMouseOver
        forceOpen={selected}
        autoUpdateOnAnimationFrame
      >
        {(props) => (
          <g transform="scale(1, -1)" {...props}>
            {/* pointer-events:none so the <use> bounding box doesn't expand the hit area */}
            <use
              href={`${ICONS_SPRITE_URL}#icon-cloudserveroutlined`}
              width={size}
              height={size}
              x={-size / 2}
              y={-size / 2}
              filter={`url(#${filterId})`}
              pointerEvents="none"
            />
            {/* sole hit target: circle sized to match the visible icon */}
            <circle cx={0} cy={0} r={size * 0.4} fill="transparent" pointerEvents="all" />
          </g>
        )}
      </AutoTooltip>
    </g>
  );
}

export default memo(Gateway3pItem);
